import { useTranslation } from 'react-i18next'

import {
  ArrowsCounterClockwiseIcon,
  ArrowSquareOutIcon,
  DotsThreeCircleVerticalIcon,
  PackageIcon,
  PencilSimpleIcon,
  XIcon,
} from '@phosphor-icons/react'

import {
  Button,
  ButtonMenu,
  ChatAlertIcon,
  ContactCarrierIcon,
  ContactCustomerIcon,
  SplitItemIcon,
} from '@reveni-io/reveni-ui'

import { actionsMenuSections } from './constants'
import { getVisibleSections } from './helpers'
import * as Styles from './styles'

const MoreActionsButton = ({
  onPrepareParcel,
  onSplitParcel,
  allowPrepareParcel,
  allowAmendPackingStatus,
  openEditOutboundDestinationAddressModal,
  allowEditOutboundDestinationAddress,
  openSendEmailModalForContactCustomer,
  openSendEmailModalForContactCarrier,
  allowSendEmail,
  openEditOutboundOriginLocationModal,
  allowEditOutboundOriginLocation,
  openEditInboundOriginAddressModal,
  allowEditInboundOriginAddress,
  openEditInboundDestinationLocationModal,
  allowEditInboundDestinationLocation,
  openEditCarrierServiceModal,
  allowEditCarrierService,
  openResetParcelModal,
  allowResetParcel,
  openCancelParcelModal,
  allowCancelParcel,
  allowSplitParcel,
  isInbound,
  onClickCarrierTrackingPage,
  displayCarrierTrackingPageAction,
  onClickViewInStore,
  viewInStoreUrl,
  onClickOrderTrackingPage,
  orderTrackingPageUrl,
  onOpenClaim,
  allowOpenClaim,
}) => {
  const { t } = useTranslation()

  const onEditOrigin = isInbound ? openEditInboundOriginAddressModal : openEditOutboundOriginLocationModal
  const allowEditOrigin = isInbound ? allowEditInboundOriginAddress : allowEditOutboundOriginLocation
  const onEditDestination = isInbound
    ? openEditInboundDestinationLocationModal
    : openEditOutboundDestinationAddressModal
  const allowEditDestination = isInbound ? allowEditInboundDestinationLocation : allowEditOutboundDestinationAddress

  const actionsMenuItems = [
    {
      label: (
        <Styles.MenuOptionLabel>
          <PackageIcon />
          {t('Preparar envío')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onPrepareParcel,
      disabled: !allowPrepareParcel,
      visible: !isInbound,
      section: 'shipment',
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <SplitItemIcon size={20} />
          {t('Partir envío')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onSplitParcel,
      disabled: !allowSplitParcel,
      visible: !isInbound,
      section: 'shipment',
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <PencilSimpleIcon />
          {t('Editar origen')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onEditOrigin,
      disabled: !allowEditOrigin,
      visible: true,
      section: 'shipment',
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <PencilSimpleIcon />
          {t('Editar destino')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onEditDestination,
      disabled: !allowEditDestination,
      visible: true,
      section: 'shipment',
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ArrowsCounterClockwiseIcon />
          {t('Reiniciar envío')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openResetParcelModal,
      disabled: !allowResetParcel,
      visible: !isInbound,
      section: 'shipment',
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <XIcon />
          {t('Cancelar envío')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openCancelParcelModal,
      disabled: !allowCancelParcel,
      visible: true,
      section: 'shipment',
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ArrowSquareOutIcon />
          {t('Ir a la página de seguimiento del transportista')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onClickCarrierTrackingPage,
      disabled: false,
      visible: !!displayCarrierTrackingPageAction,
      section: 'shipment',
    },

    {
      label: (
        <Styles.MenuOptionLabel>
          <ArrowSquareOutIcon />
          {t('Ver en tienda')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onClickViewInStore,
      disabled: false,
      visible: !!viewInStoreUrl,
      section: 'order',
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ArrowSquareOutIcon />
          {t('Abrir página de seguimiento del pedido')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onClickOrderTrackingPage,
      disabled: false,
      visible: !!orderTrackingPageUrl,
      section: 'order',
    },

    {
      label: (
        <Styles.MenuOptionLabel>
          <ContactCustomerIcon size={16} />
          {t('Contactar con el cliente')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openSendEmailModalForContactCustomer,
      disabled: !allowSendEmail,
      visible: true,
      section: 'customer',
    },

    {
      label: (
        <Styles.MenuOptionLabel>
          <PencilSimpleIcon />
          {t('Editar servicio de logística')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openEditCarrierServiceModal,
      disabled: !allowEditCarrierService,
      visible: true,
      section: 'logisticService',
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ContactCarrierIcon size={16} />
          {t('Contactar con el transportista')}
        </Styles.MenuOptionLabel>
      ),
      onClick: openSendEmailModalForContactCarrier,
      disabled: !allowSendEmail,
      visible: true,
      section: 'logisticService',
    },
    {
      label: (
        <Styles.MenuOptionLabel>
          <ChatAlertIcon size="16" />
          {t('Abrir una incidencia')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onOpenClaim,
      disabled: !allowOpenClaim,
      visible: true,
      section: 'logisticService',
    },

    {
      label: (
        <Styles.MenuOptionLabel>
          <PencilSimpleIcon />
          {t('Corregir estado de empaquetado')}
        </Styles.MenuOptionLabel>
      ),
      onClick: onPrepareParcel,
      disabled: !allowAmendPackingStatus,
      visible: !isInbound,
      section: 'products',
    },
  ]

  const visibleActionsItems = actionsMenuItems.filter(item => item.visible)

  const visibleSections = getVisibleSections(actionsMenuSections, visibleActionsItems).map(section => ({
    ...section,
    label: t(section.label),
  }))

  return (
    <ButtonMenu
      stopPropagation
      groupBySections
      sections={visibleSections}
      menuItems={visibleActionsItems}
      renderButton={onClick => (
        <Button onClick={onClick} variant="text">
          <DotsThreeCircleVerticalIcon size={24} weight="light" data-testid="more-actions-button" /> {t('Más acciones')}
        </Button>
      )}
    />
  )
}

export default MoreActionsButton
